import { useEffect } from "react";
import { prefersReducedMotion, useTour, waitForElement } from "@tour-kit/react";

const SCROLL_WAIT_MS = 5000;

const scrollElementIntoView = (el: HTMLElement) => {
  el.scrollIntoView({
    behavior: prefersReducedMotion() ? "auto" : "smooth",
    block: "center",
    inline: "nearest",
  });
};

/**
 * Auto-scrolls the active step's target into view. tour-kit ships a `scroll`
 * config + `scrollIntoView` util but never invokes them in the step engine, so
 * off-screen targets stay off-screen. We drive it from `currentStep.target`.
 *
 * `resolveTarget` / `isVisibleStep` are not runtime-exported in the installed
 * version, so we branch on the target shape ourselves; string selectors (our
 * data registry) go through `waitForElement` to handle lazy / tab-switch DOM.
 */
const TourAutoScroll = () => {
  const { isActive, currentStep } = useTour();
  const target = currentStep?.target ?? null;

  useEffect(() => {
    if (!isActive || target == null) {
      return;
    }

    if (typeof target === "string") {
      const controller = new AbortController();
      waitForElement(target, SCROLL_WAIT_MS, controller.signal)
        .then(scrollElementIntoView)
        .catch(() => {
          // target never mounted or step changed — nothing to scroll to
        });
      return () => controller.abort();
    }

    const el = typeof target === "function" ? target() : target.current;
    if (el) {
      scrollElementIntoView(el);
    }
  }, [isActive, target]);

  return null;
};

export default TourAutoScroll;
